import { computed, reactive, ref } from 'vue'
import { defineStore } from 'pinia'
import { dialog, i18N, toast } from '@/common/util'
import competenciasService from '@/services/competencias.service'

import type { CompetenciaType, ListBasicType, LoaderType, SkillsBasicType } from '@/types/types'

export const useCompetenciasStore = defineStore('competencias', () => {
  /**
   * States
   */
  const list = ref<CompetenciaType[]>([])
  const basicList = ref<{
    files: ListBasicType[]
    courses: ListBasicType[]
    trails: ListBasicType[]
    assessments: ListBasicType[]
  }>({
    files: [],
    courses: [],
    trails: [],
    assessments: []
  })

  const status = reactive({
    list: 'initial' as LoaderType,
    user: 'initial' as LoaderType,
    create: 'initial' as LoaderType,
    update: 'initial' as LoaderType,
    remove: 'initial' as LoaderType,
    get: 'initial' as LoaderType,
    basicList: {
      all: 'initial' as LoaderType,
      files: 'initial' as LoaderType,
      courses: 'initial' as LoaderType,
      trails: 'initial' as LoaderType,
      assessments: 'initial' as LoaderType
    }
  })

  /**
   * Getters
   */
  const loading = computed(() => Object.keys(status).some(key => status[key] === 'loading'))
  const loadingBasicList = computed(() =>  Object.values(status.basicList).some(value => value === 'loading'))

  let lastFetched: number | null = null
  const lazyList = computed(() => {
    if (status.list === 'initial' || status.list === 'error') {
      getAll()
      return []
    }

    if (lastFetched && Date.now() - lastFetched > 2 * 60 * 1000) {
      getAll()
    }

    return list.value || []
  })

  const lazyFilter = ref('')
  const lazyBasicList = computed(() => {
    return (type?: 'files' | 'courses' | 'trails' | 'assessments' | 'all', skipOrigin?: boolean) => {
      if (!type) type = 'all'

      const fId = `type:${type};origin:${skipOrigin ? 'all' : 'Proprio'}`

      if (status.basicList[type] === 'initial' || lazyFilter.value !== fId) {
        lazyFilter.value = fId
        if (!skipOrigin) getBasicList({ origem: 'Proprio', type: type })
        else getBasicList({ type: type })

        return []
      }

      return basicList.value[type]
    }
  })

  /**
   * Actions
   */
  const create = async (objData: Omit<CompetenciaType, 'id'>) => {
    status.create = 'loading'

    try {
      const { data, success } = await competenciasService.post<CompetenciaType>(undefined, objData)

      if (success) {
        toast.success({message: i18N('toasts.createSuccess')})
        getAll()
        emptyList()
      }

      status.create = 'success'

      return data
    } catch {
      status.create = 'error'
    }
  }

  const update = async (objData: CompetenciaType, id: string) => {
    status.update = 'loading'

    try {
      const { data, success } = await competenciasService.put(id, objData)
      if (success) {
        const value = list.value.find(el => el.id === data.id)
        value && Object.assign(value, data)
        toast.success({message: i18N('toasts.updateSuccess')})
        emptyList()
      }

      status.update = 'success'
      return data
    } catch {
      status.update = 'error'
    }
  }

  const getAll = async (): Promise<void> => {
    status.list = 'loading'

    try {
      const { data } = await competenciasService.get()
      list.value = data as CompetenciaType[]

      lastFetched = Date.now()

      status.list = 'success'
    } catch {
      status.list = 'error'
    }
  }

  const remove = async (competencia: CompetenciaType, force?: boolean) => {
    if(!force)
      return dialog.open({
        title: i18N('feedback.attention'),
        message: i18N('dialogs.delete.message', {type: i18N('labels.skill').toLowerCase(), name: competencia.nome}),
        complete: () => remove(competencia, true)
      })

    status.remove = 'loading'
    try {
      const { success } = await competenciasService.delete(competencia.id)

      if (success) {
        toast.success({message: i18N('toasts.removeSuccess')})
        list.value.splice(list.value.findIndex(el => el.id === competencia.id), 1)
        emptyList()
      }

      status.remove = 'success'
    } catch {
      status.remove = 'error'
    }
  }

  const getById = async (id: string) => {
    status.get = 'loading'

    try {
      const { data } = await competenciasService.get(id)
      status.get = 'success'
      return data as CompetenciaType
    } catch {
      status.get = 'error'
    }
  }

  const emptyList = () => {
    Object.keys(status.basicList).forEach((key) => status.basicList[key] = 'initial')
    Object.keys(basicList.value).forEach((key) => basicList.value[key].splice(0))
  }

  const getBasicList = (params?: SkillsBasicType) => {
    if (!params) params = {}
    if (!params?.type) params.type = 'all'

    status.basicList[params.type] = 'loading'

    return competenciasService.getListBasic({ ...params })
      .then(({ data }) => {
        if (params.type) {
          basicList.value[params.type] = (data || []).map(c => ({ value: c.id, label: c.nome }))
          status.basicList[params.type] = 'success'
        }
        return basicList.value
      })
      .catch(() => {
        if (params.type) {
          status.basicList[params.type] = 'error'
        }
      })
  }

  const getAllBySection = async (sectionId: string) => {
    try {
      const { data } = await competenciasService.getAllBySection(sectionId)
      return data
    } catch {
      //
    }
  }

  return {
    list,
    status,
    // getters,
    loading,
    lazyBasicList,
    loadingBasicList,
    // Actions
    create,
    update,
    getAll,
    getById,
    remove,
    getAllBySection,
    lazyList,
    getBasicList,
    getListBasic: getBasicList
  }
})

export default useCompetenciasStore
